import { styled } from "goober";
import { usePendingRequestCount } from "../../hooks/usePendingRequestCount";
import { useMakeUrl, routes, useRouteMatch } from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { CountBadge } from "../CountBadge";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { MainScrollContainer } from "../MainScrollContainer";
import { Text } from "../Text";

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderInner = styled("div")`
  display: flex;
  align-items: center;
`;

const Tabs = styled("div")`
  display: flex;
  gap: 8px;
  padding: 12px;
  overflow: hidden;
`;

const Tab = styled(Link)`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  border: none;
  text-decoration: none;
  outline: none;
  background: none;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 24px;
  cursor: pointer;
  color: var(--text-secondary);

  &.active {
    color: var(--text-primary);
    background: var(--alpha4);
  }

  &:hover {
    background: linear-gradient(var(--alpha4)), var(--surface-primary);
    color: var(--text-primary);
  }
`;

const TABS = [
  {
    route: routes.monitoring.workflows,
    label: "Workflows",
    icon: "product-workflows",
  },
  {
    route: routes.monitoring.connectors,
    label: "Connectors",
    icon: "api-key-custom",
  },
  { route: routes.monitoring.access, label: "Access map", icon: "orbit" },
  { route: routes.monitoring.value, label: "Savings", icon: "wallet" },
  { route: routes.monitoring.ai, label: "AI", icon: "brain" },
  {
    route: routes.monitoring.requests,
    label: "Requests",
    icon: "heart-handshake",
  },
] as const;

type Props = {
  children: React.ReactNode;
  headerItem?: React.ReactNode;
  // Draws a hairline under the tab strip. Full-bleed list tabs (Requests) want it
  // to separate the sticky list header from the tabs; the carded dashboard tabs
  // don't, so it's opt-in.
  headerDivider?: boolean;
};

export function MonitoringLayout({
  children,
  headerItem,
  headerDivider,
}: Props) {
  const pendingCount = usePendingRequestCount();
  return (
    <Backdrop permanentNavigation>
      <Container>
        <div
          style={
            headerDivider
              ? { borderBottom: "0.5px solid var(--border-alpha)" }
              : undefined
          }
        >
          <HeaderInner>
            <Tabs>
              {TABS.map(({ route, label, icon }) => (
                <MonitoringTab
                  key={label}
                  route={route}
                  label={label}
                  icon={icon}
                  count={
                    route === routes.monitoring.requests ? pendingCount : 0
                  }
                />
              ))}
            </Tabs>
            {headerItem}
          </HeaderInner>
        </div>
        <MainScrollContainer>{children}</MainScrollContainer>
      </Container>
    </Backdrop>
  );
}

// scope rides the parent route, so makeUrl carries it across tab navigation.
function MonitoringTab({
  route,
  label,
  icon,
  count,
}: {
  route: (typeof TABS)[number]["route"];
  label: string;
  icon: (typeof TABS)[number]["icon"];
  count: number;
}) {
  const makeUrl = useMakeUrl();
  const [active] = useRouteMatch(route);

  return (
    <Tab to={makeUrl(route)} className={active ? "active" : ""}>
      <Icon name={icon} size={16} />
      <Text variant="text-small-md">{label}</Text>
      {count > 0 ? (
        <CountBadge>
          <Text variant="text-smaller-md">{count}</Text>
        </CountBadge>
      ) : null}
    </Tab>
  );
}
